import { useCallback, useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useBulkActionsState } from 'src/components/common/BulkActions/useBulkActionsState';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Button } from 'src/core/ui';
import { useRetailSaleListingTickets } from 'src/hooks/api/useRetailSaleListingTickets';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import * as loadingStyles from 'src/modals/RetailSale/components/MultiListingRetailSaleCheckoutStep.css';
import { RetailSaleWithProvider } from 'src/modals/RetailSale/RetailSale';
import { getRetailExternalSaleId } from 'src/modals/RetailSale/RetailSaleLocalContext.utils';
import {
  MultiListingSelectionRow,
  RetailSaleFormValues,
} from 'src/modals/RetailSale/types';
import { ContentId } from 'src/utils/constants/contentId';
import { EmptyListingQuery } from 'src/utils/eventQueryUtils';
import {
  Feature,
  ListingQuery,
  RetailSaleListingTickets,
  Ticket,
  TicketType,
} from 'src/WebApiController';

import { useInventoryEventBulkActionsState } from '../../InventoryEventBulkActions/useInventoryEventBulkActionsState';

/**
 * Bulk action that opens the RetailSale modal in multi-listing mode
 * (POS-8649 — Sales Group multi-event checkout).
 *
 * Button enablement mirrors the other bulk actions — gated on
 * `areBulkActionsEnabled` alone, no ID lookups. All resolving of
 * listing details is deferred to the modal so the inventory page
 * doesn't burn API calls on every unrelated selection change.
 *
 * Click flow:
 *   1. Fast path: use `affectedVisibleListingIds` if already populated
 *      (individual listings selected, or events the user has expanded).
 *      Build a ListingQuery with entityIds and pass it to the modal.
 *   2. Slow path: for event-only selections, pass filterQueryWithEventIds
 *      directly. The modal fetches all listings via a single batch call to
 *      GetListingsSectionalDataForEvents.
 */
export const RetailSaleActions = () => {
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);
  const { areBulkActionsEnabled } = useBulkActionsState();
  const { setModal } = useContext(ModalContext);

  const {
    affectedVisibleListingIds,
    filterQueryWithEventIds,
    hasSelectedPlaceholderEvents,
  } = useInventoryEventBulkActionsState();

  const handleClick = useCallback(() => {
    if (!areBulkActionsEnabled) return;

    // Fast path: individual listings already resolved from catalog context.
    // Slow path: event-only selection — pass the event filter so the modal
    // fetches all listings via GetListingsSectionalDataForEvents.
    const filterQuery: ListingQuery =
      affectedVisibleListingIds.length > 0
        ? { ...EmptyListingQuery, entityIds: affectedVisibleListingIds }
        : filterQueryWithEventIds;

    // Guard: nothing selected
    if (
      (filterQuery.entityIds == null || filterQuery.entityIds.length === 0) &&
      (filterQuery.eventOrMappingIds == null ||
        filterQuery.eventOrMappingIds.length === 0)
    ) {
      return;
    }

    setModal({
      children: <MultiListingRetailSaleModal filterQuery={filterQuery} />,
      clickOutsideToClose: true,
      size: 'slide-in',
    });
  }, [
    affectedVisibleListingIds,
    areBulkActionsEnabled,
    filterQueryWithEventIds,
    setModal,
  ]);

  if (!hasRetailSaleFeature) {
    return null;
  }

  // Placeholder events are synthesized and have no sellable inventory, so a
  // retail sale can't be created against them — disable the action when any
  // selected event is a placeholder.
  const isDisabled = !areBulkActionsEnabled || hasSelectedPlaceholderEvents;

  return (
    <Button variant="outline" disabled={isDisabled} onClick={handleClick}>
      <Content id={ContentId.RetailSale} />
    </Button>
  );
};

const getQuantitySold = (perListingSelections: MultiListingSelectionRow[]) => {
  const ticketIds = perListingSelections.flatMap((sel) =>
    sel.availableTicketIds.slice(0, sel.qtyToSell)
  );
  const placeholderTicketCount = perListingSelections
    .filter((s) => Boolean(s.isPlaceholder))
    .reduce((total, s) => {
      total += s.qtyToSell;
      return total;
    }, 0);

  return ticketIds.length + placeholderTicketCount;
};

/**
 * Multi-listing entry point. Mirrors the single-listing `<RetailSale>`
 * component in RetailSale.tsx: mount `<FormProvider>` synchronously with
 * empty defaults, then `methods.reset(...)` in a useEffect once listing
 * details have loaded. Keeping the FormProvider tree stable across the
 * loading transition matters — the Adyen Drop-In initiates a fetch inside
 * a useEffect after the form user hits Pay & Complete, and any remount of
 * the subtree while that fetch is in flight tears it down before it can
 * receive its config, producing `TypeError: Failed to fetch` on the
 * checkoutshopper session-setup call.
 */
export const MultiListingRetailSaleModal = ({
  filterQuery,
  onSaleCreated,
}: {
  filterQuery: ListingQuery;
  /**
   * POS-9099: completion signal for the chained "Add Retail Sale" workflow — forwarded
   * to the retail create flow so the orchestrator can mark the sale committed (and skip
   * rolling back the seeded inventory). Undefined for the normal bulk-action entry point.
   */
  onSaleCreated?: () => void;
}) => {
  const { data: listingDetails, isLoading } =
    useRetailSaleListingTickets(filterQuery);

  const methods = useForm<RetailSaleFormValues>({
    defaultValues: {},
  });

  useEffect(() => {
    if (!listingDetails || listingDetails.length === 0) return;

    const perListingSelections = listingDetails.flatMap((ld) =>
      buildPerListingSelectionsForListing(ld)
    );

    const ticketIds = perListingSelections.flatMap((sel) =>
      sel.availableTicketIds.slice(0, sel.qtyToSell)
    );

    const allListingIds = listingDetails.map((ld) => ld.listingId);
    const primaryListingId = allListingIds[0] ?? 0;
    const quantitySold = getQuantitySold(perListingSelections);

    // totalNetProceeds seed = Σ(qtyToSell × unitProceeds). Source of truth
    // in multi-listing mode (RetailSaleBuyerStep hides its manual
    // totalNetProceeds input); MultiListingRetailSaleCheckoutStep keeps
    // it reactive as the seller edits per-row values.
    const totalNetProceedsSum = perListingSelections.reduce(
      (sum, sel) => sum + sel.qtyToSell * sel.unitProceeds,
      0
    );
    const totalNetProceeds =
      totalNetProceedsSum > 0 ? totalNetProceedsSum : undefined;

    const primaryListing = listingDetails.find(
      (ld) => ld.listingId === primaryListingId
    );
    const ticketType =
      primaryListing?.topPriTktType ??
      listingDetails[0]?.topPriTktType ??
      TicketType.ETicket;
    const currencyCode =
      primaryListing?.currency ?? listingDetails[0]?.currency ?? 'USD';

    // POS-8322: Default in-hand date from the primary listing when there's
    // a single listing. For multi-listing, the in-hand dates may differ across
    // listings so we leave it unset and let the backend fall back per-listing.
    const inHandDate =
      allListingIds.length === 1
        ? (primaryListing?.inHandAt ?? undefined)
        : undefined;

    methods.reset({
      listingId: primaryListingId,
      // POS-8649 — Bug fix #4: seed externalSaleId with an empty string so
      // react-hook-form's register() never warns about an uncontrolled input.
      externalSaleId: primaryListingId
        ? getRetailExternalSaleId(primaryListingId)
        : '',
      // ticketIds presence marks multi-listing mode (see
      // RetailSaleLocalContext.tsx bug fix #2).
      ticketIds,
      allListingIds,
      perListingSelections,
      currencyCode,
      saleDate: new Date().toISOString(),
      useShippingAddressOnFile: false,
      billingAddressSameAsShipping: true,
      marketplacePaymentLines: [],
      ticketType,
      quantitySold,
      totalNetProceeds,
      inHandDate,
      paymentMethodChoice: 'adyen',
      externalPaymentMethodType: null,
      externalPaymentNote: null,
      externalPaymentAmount:
        totalNetProceeds != null && totalNetProceeds > 0
          ? totalNetProceeds
          : null,
      externalPaymentDate: new Date().toISOString().split('T')[0],
    });
  }, [listingDetails, methods]);

  if (isLoading || !listingDetails || listingDetails.length === 0) {
    return (
      <div className={loadingStyles.modalLoading}>
        <PosSpinner />
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <RetailSaleWithProvider onSaleCreated={onSaleCreated} />
    </FormProvider>
  );
};

/**
 * Groups a listing's tickets by (section, row) and returns one
 * MultiListingSelectionRow per group. Grouping key follows the same
 * convention as the previous #5707 prototype's inline logic: section
 * from `ld.seating.section`, row from `ticket.row ?? ld.seating.row`.
 *
 * Within each group tickets are ordered by `lstOrd` so the seat-range
 * autofill and the `availableTicketIds.slice(0, qtyToSell)` derivation
 * stay consistent with `getConsecutiveSeatingsForQuantity` (which sorts
 * by `lstOrd`).
 *
 * Default `qtyToSell` = group size (broker's full selection); if they
 * lower the number in the table, the submit path trims from the end.
 */
const buildPerListingSelectionsForListing = (
  ld: RetailSaleListingTickets
): MultiListingSelectionRow[] => {
  const listingId = ld.listingId;
  const ldSection = ld.seating?.section ?? null;
  const ldRow = ld.seating?.row ?? null;

  // POS-9099: a seat-saver (placeholder) listing has no ticket rows — build a single
  // quantity-driven row from its seating + available quantity so it can be sold by qty.
  if (ld.isSeatSaver) {
    const qty = ld.availableQuantity ?? 0;
    if (qty <= 0) return [];
    return [
      {
        listingId,
        section: ldSection,
        row: ldRow,
        availableTicketIds: [],
        availableTicketSeats: [],
        qtyToSell: qty,
        seatFrom: ld.seating?.seatFr ?? null,
        seatTo: ld.seating?.seatTo ?? null,
        unitProceeds: ld.listPrice || ld.allInPrice || ld.unitCst?.amt || 0,
        isPlaceholder: true,
      },
    ];
  }

  const groups: Record<
    string,
    { section: string | null; row: string | null; tickets: Ticket[] }
  > = {};

  // Only include tickets that can actually be sold. Mirrors resolveSaleTicketIds
  // in RetailSaleLocalContext.utils.ts — a listing's tickets array still returns
  // sold/held/spoiled/wasted rows, and the backend rejects the sales-group create
  // with "Tickets <id> are not available for allocation" if we forward them.
  const availableTickets = (ld.tickets ?? []).filter(
    (t) =>
      t.saleId == null &&
      t.holdId == null &&
      t.spoiledSaleId == null &&
      !t.isWasted
  );

  availableTickets.forEach((t) => {
    const section = ldSection;
    const row = t.row ?? ldRow;
    const key = `${listingId}||${section ?? ''}||${row ?? ''}`;
    if (!groups[key]) {
      groups[key] = { section, row, tickets: [] };
    }
    groups[key].tickets.push(t);
  });

  const unitProceedsSeed =
    ld.listPrice || ld.allInPrice || ld.unitCst?.amt || 0;

  return Object.values(groups).map((g): MultiListingSelectionRow => {
    const sortedTickets = g.tickets.toSorted(
      (a, b) => (a.lstOrd ?? a.ord) - (b.lstOrd ?? b.ord)
    );
    const availableTicketIds = sortedTickets
      .map((t) => t.id)
      .filter((id): id is number => id > 0);
    const availableTicketSeats = sortedTickets.map((t) => t.seat ?? null);
    const firstSeat = sortedTickets[0]?.seat ?? null;
    const lastSeat = sortedTickets[sortedTickets.length - 1]?.seat ?? null;
    return {
      listingId,
      section: g.section,
      row: g.row,
      availableTicketIds,
      availableTicketSeats,
      qtyToSell: availableTicketIds.length,
      seatFrom: firstSeat,
      seatTo: lastSeat,
      unitProceeds: unitProceedsSeed,
    };
  });
};
